import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';

interface DocumentUploadDialogProps {
  isOpen: boolean;
  docType: string;
  onDateSelected: (date: string, docType: string) => void;
  onClose: () => void;
  requiresDate?: boolean;
}

/**
 * Reusable Document Upload Dialog with Date Picker
 * 
 * This component provides a calendar-based date selection flow for document uploads.
 * After date selection, it triggers the file input automatically.
 */
const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  isOpen,
  docType,
  onDateSelected,
  onClose,
  requiresDate = true,
}) => {
  const handleDateSelect = (newValue: any) => {
    if (newValue) {
      const formattedDate = newValue.toISOString().split('T')[0];
      onDateSelected(formattedDate, docType);

      // Close dialog and trigger file input after date selection
      setTimeout(() => {
        const fileInput = document.getElementById(`${docType}-file`) as HTMLInputElement;
        if (fileInput) {
          fileInput.click();
        }
        onClose();
      }, 150);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          margin: { xs: '10px', sm: '32px' },
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ mb: { xs: 1, sm: 2 }, textAlign: 'center', fontWeight: 'bold' }}
        >
          Select Expiry Date
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              transform: { xs: 'scale(0.85)', sm: 'scale(1)' },
              transformOrigin: 'top center',
              mb: { xs: -4, sm: 0 },
            }}
          >
            <DateCalendar onChange={handleDateSelect} />
          </Box>
        </LocalizationProvider>
      </Box>
    </Dialog>
  );
};

export default DocumentUploadDialog;
